/**
 * SHEEP MADE SIMPLE
 * SMSGrazingEngine
 *
 * Version: 1.0.0
 *
 * Responsibilities:
 * - Read confirmed My Block grazing inputs
 * - Calculate current and maximum rotation length
 * - Resolve the active grazing target
 * - Project the flock's current position in the saved rotation
 * - Identify practical split and pressure candidates
 * - Return one calm, prioritised grazing recommendation
 *
 * This module does NOT:
 * - Read from or write to SMSFarmStorage
 * - Store calculated values
 * - Render HTML
 * - Depend on Shopify
 * - Mutate the supplied profile
 */

window.SMSGrazingEngine = (() => {
  "use strict";

  const ENGINE_VERSION = "1.0.0";

  const DAY_MS = 86400000;

  const SEASONS = Object.freeze([
    "spring",
    "summer",
    "autumn",
    "winter"
  ]);

  const DEFAULT_TARGETS = Object.freeze({
    spring: Object.freeze({
      minimumDays: 21,
      preferredDays: 28,
      maximumDays: 28
    }),
    summer: Object.freeze({
      minimumDays: 28,
      preferredDays: 35,
      maximumDays: 35
    }),
    autumn: Object.freeze({
      minimumDays: 35,
      preferredDays: 42,
      maximumDays: 42
    }),
    winter: Object.freeze({
      minimumDays: 49,
      preferredDays: 56,
      maximumDays: 56
    })
  });

  const DURATION_DAYS = Object.freeze({
    half: 3.5,
    one: 7,
    two: 14,
    three: 21
  });

  const GROWTH_CONDITION_TARGETS = Object.freeze({
    very_slow: Object.freeze({
      minimumDays: 49,
      preferredDays: 56,
      maximumDays: 63,
      label: "Very slow growth"
    }),
    slow: Object.freeze({
      minimumDays: 42,
      preferredDays: 49,
      maximumDays: 56,
      label: "Slow growth"
    }),
    growth_still_slow: Object.freeze({
      minimumDays: 42,
      preferredDays: 49,
      maximumDays: 56,
      label: "Growth still slow"
    }),
    growth_beginning_to_lift: Object.freeze({
      minimumDays: 35,
      preferredDays: 42,
      maximumDays: 49,
      label: "Growth beginning to lift"
    }),
    steady: Object.freeze({
      minimumDays: 28,
      preferredDays: 35,
      maximumDays: 42,
      label: "Steady growth"
    }),
    growth_steady: Object.freeze({
      minimumDays: 28,
      preferredDays: 35,
      maximumDays: 42,
      label: "Steady growth"
    }),
    fast: Object.freeze({
      minimumDays: 21,
      preferredDays: 28,
      maximumDays: 35,
      label: "Fast growth"
    }),
    strong_spring_growth: Object.freeze({
      minimumDays: 21,
      preferredDays: 28,
      maximumDays: 35,
      label: "Strong spring growth"
    }),
    grass_getting_ahead: Object.freeze({
      minimumDays: 21,
      preferredDays: 28,
      maximumDays: 35,
      label: "Grass getting ahead"
    }),
    growth_slowing: Object.freeze({
      minimumDays: 35,
      preferredDays: 42,
      maximumDays: 49,
      label: "Growth slowing"
    }),
    dry: Object.freeze({
      minimumDays: 42,
      preferredDays: 49,
      maximumDays: 56,
      label: "Dry conditions"
    }),
    dry_conditions: Object.freeze({
      minimumDays: 42,
      preferredDays: 49,
      maximumDays: 56,
      label: "Dry conditions"
    }),
    drought: Object.freeze({
      minimumDays: 49,
      preferredDays: 56,
      maximumDays: 63,
      label: "Drought conditions"
    })
  });

  const PRESSURE_REASON_WEIGHTS = Object.freeze({
    larger: 5,
    grows_more_grass: 5,
    better_soil: 3,
    better_shelter: 2,
    better_drainage: 3,
    fewer_weeds: 2
  });

  function isPlainObject(value) {
    return (
      value !== null &&
      typeof value === "object" &&
      !Array.isArray(value)
    );
  }

  function clone(value) {
    if (value === undefined) return undefined;

    if (typeof structuredClone === "function") {
      return structuredClone(value);
    }

    return JSON.parse(JSON.stringify(value));
  }

  function finiteNumber(value, fallback = null) {
    const number = Number(value);

    return Number.isFinite(number)
      ? number
      : fallback;
  }

  function positiveNumber(value, fallback = null) {
    const number = finiteNumber(value, fallback);

    return number !== null && number > 0
      ? number
      : fallback;
  }

  function integer(value, fallback = null) {
    const number = Number(value);

    return Number.isInteger(number)
      ? number
      : fallback;
  }

  function clamp(value, minimum, maximum) {
    return Math.min(
      maximum,
      Math.max(minimum, value)
    );
  }

  function round(value, decimals = 1) {
    const factor = 10 ** decimals;

    return Math.round(value * factor) / factor;
  }

  function normaliseToken(value) {
    return String(value || "")
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, "_");
  }

  function valueAt(object, path, fallback = undefined) {
    if (!path) {
      return object === undefined
        ? fallback
        : object;
    }

    const value = String(path)
      .split(".")
      .filter(Boolean)
      .reduce(
        (current, key) =>
          current == null
            ? undefined
            : current[key],
        object
      );

    return value === undefined
      ? fallback
      : value;
  }

  function firstDefined(values, fallback = undefined) {
    for (const value of values) {
      if (
        value !== undefined &&
        value !== null &&
        value !== ""
      ) {
        return value;
      }
    }

    return fallback;
  }

  function getMyBlock(profile) {
    return isPlainObject(profile?.myBlock)
      ? profile.myBlock
      : {};
  }

  function getGrazing(profile) {
    const myBlock = getMyBlock(profile);

    return isPlainObject(myBlock.grazing)
      ? myBlock.grazing
      : {};
  }

  function getImportantPaddocks(profile) {
    const myBlock = getMyBlock(profile);

    return isPlainObject(myBlock.importantPaddocks)
      ? myBlock.importantPaddocks
      : {};
  }

  function getActivePaddocks(profile) {
    const myBlock = getMyBlock(profile);
    const paddocks = Array.isArray(myBlock.paddocks)
      ? myBlock.paddocks
      : [];

    return paddocks
      .filter(paddock => {
        if (!isPlainObject(paddock)) return false;
        if (!paddock.id) return false;

        return paddock.active !== false &&
          paddock.active !== "no";
      })
      .map((paddock, sourceIndex) => ({
        ...clone(paddock),
        sourceIndex,
        rotationOrder: resolveRotationOrder(
          paddock,
          sourceIndex
        )
      }))
      .sort((a, b) => {
        if (a.rotationOrder !== b.rotationOrder) {
          return a.rotationOrder - b.rotationOrder;
        }

        return a.sourceIndex - b.sourceIndex;
      });
  }

  function resolveRotationOrder(paddock, sourceIndex) {
    const candidate = firstDefined([
      paddock.rotationOrder,
      paddock.order,
      paddock.position,
      paddock.rotationPosition
    ]);

    const resolved = finiteNumber(candidate);

    return resolved === null
      ? sourceIndex
      : resolved;
  }

  function durationFromCustomFields(paddock) {
    const days = positiveNumber(
      firstDefined([
        paddock.normalDurationDays,
        paddock.typicalGrazingDays,
        paddock.grazingDurationDays,
        paddock.normalStayDays
      ])
    );

    if (days !== null) {
      return {
        days,
        source: "explicit_days",
        known: true
      };
    }

    const weeks = positiveNumber(
      firstDefined([
        paddock.normalDurationWeeks,
        paddock.typicalGrazingWeeks,
        paddock.grazingDurationWeeks,
        paddock.normalStayWeeks
      ])
    );

    if (weeks !== null) {
      return {
        days: weeks * 7,
        source: "explicit_weeks",
        known: true
      };
    }

    return null;
  }

  function getPaddockDuration(paddock) {
    const custom = durationFromCustomFields(paddock);

    if (custom) {
      return custom;
    }

    const token = normaliseToken(
      firstDefined([
        paddock.normalDuration,
        paddock.typicalGrazingDuration,
        paddock.normalStay
      ])
    );

    if (DURATION_DAYS[token] !== undefined) {
      return {
        days: DURATION_DAYS[token],
        source: "duration_token",
        known: true
      };
    }

    if (token === "variable") {
      return {
        days: 7,
        source: "variable_default",
        known: false
      };
    }

    if (token === "unsure" || token === "") {
      return {
        days: 7,
        source: "unknown_default",
        known: false
      };
    }

    const parsed = positiveNumber(token);

    if (parsed !== null) {
      return {
        days: parsed,
        source: "numeric_token",
        known: true
      };
    }

    return {
      days: 7,
      source: "fallback",
      known: false
    };
  }

  function buildRotationEntries(profile) {
    return getActivePaddocks(profile).map(
      (paddock, index) => {
        const duration = getPaddockDuration(paddock);

        return {
          paddockId: paddock.id,
          paddockName:
            String(paddock.name || "").trim() ||
            `Paddock ${index + 1}`,
          order: index + 1,
          rotationOrder: paddock.rotationOrder,
          durationDays: duration.days,
          durationWeeks: duration.days / 7,
          durationKnown: duration.known,
          durationSource: duration.source,
          area: positiveNumber(paddock.area, null),
          paddock
        };
      }
    );
  }

  function sumBy(items, selector) {
    return items.reduce(
      (total, item) =>
        total + finiteNumber(selector(item), 0),
      0
    );
  }

  function formatWeeks(days) {
    return round(days / 7, 1);
  }

  function getCurrentRotation(profile) {
    const entries = buildRotationEntries(profile);
    const days = sumBy(
      entries,
      entry => entry.durationDays
    );

    const knownDurationCount = entries.filter(
      entry => entry.durationKnown
    ).length;

    return {
      days: round(days, 1),
      weeks: formatWeeks(days),
      activePaddockCount: entries.length,
      grazingShiftCount: entries.length,
      knownDurationCount,
      assumedDurationCount:
        entries.length - knownDurationCount,
      complete:
        entries.length > 0 &&
        knownDurationCount === entries.length,
      entries: entries.map(entry => ({
        paddockId: entry.paddockId,
        paddockName: entry.paddockName,
        order: entry.order,
        durationDays: round(entry.durationDays, 1),
        durationWeeks: round(
          entry.durationWeeks,
          2
        ),
        durationKnown: entry.durationKnown,
        durationSource: entry.durationSource
      }))
    };
  }

  function practicalSplitCount(paddock) {
    if (
      paddock.canSplit !== true &&
      normaliseToken(paddock.canSplit) !== "yes"
    ) {
      return 1;
    }

    const count = integer(
      firstDefined([
        paddock.practicalSplitCount,
        paddock.maximumTemporaryShifts,
        paddock.splitCount
      ]),
      null
    );

    return count !== null && count >= 2
      ? count
      : 2;
  }

  function splitHasUsableWater(paddock) {
    const answer = normaliseToken(
      firstDefined([
        paddock.splitWaterAccess,
        paddock.waterInEveryTemporaryShift
      ])
    );

    return answer === "yes" ||
      answer === "temporary";
  }

  function getSplitCandidates(profile) {
    const grazing = getGrazing(profile);
    const fencingAvailable = normaliseToken(
      grazing.temporaryFencingAvailable
    );
    const confidence = normaliseToken(
      grazing.temporaryFencingConfidence
    );

    const fencingUsable =
      fencingAvailable === "yes" &&
      confidence !== "not_confident";

    return buildRotationEntries(profile)
      .filter(entry => {
        const paddock = entry.paddock;

        return (
          paddock.canSplit === true ||
          normaliseToken(paddock.canSplit) === "yes"
        );
      })
      .map(entry => {
        const count = practicalSplitCount(
          entry.paddock
        );
        const extraShifts = Math.max(0, count - 1);
        const waterSuitable = splitHasUsableWater(
          entry.paddock
        );
        const gainDays =
          extraShifts * entry.durationDays;

        let suitability = "possible";
        let blockedReason = null;

        if (!fencingUsable) {
          suitability = "not_currently_practical";
          blockedReason =
            fencingAvailable !== "yes"
              ? "Temporary fencing is not currently available."
              : "Temporary-fencing confidence is currently low.";
        } else if (!waterSuitable) {
          suitability = "needs_water_check";
          blockedReason =
            "Water access has not been confirmed for every temporary shift.";
        } else {
          suitability = "ready";
        }

        return {
          paddockId: entry.paddockId,
          paddockName: entry.paddockName,
          area: entry.area,
          currentShiftDays: round(
            entry.durationDays,
            1
          ),
          practicalShiftCount: count,
          additionalShiftCount: extraShifts,
          potentialGainDays: round(
            gainDays,
            1
          ),
          potentialGainWeeks: formatWeeks(
            gainDays
          ),
          waterSuitable,
          fencingUsable,
          suitability,
          blockedReason
        };
      })
      .sort((a, b) => {
        const suitabilityOrder = {
          ready: 0,
          needs_water_check: 1,
          not_currently_practical: 2
        };

        const suitabilityDifference =
          suitabilityOrder[a.suitability] -
          suitabilityOrder[b.suitability];

        if (suitabilityDifference !== 0) {
          return suitabilityDifference;
        }

        if (
          b.potentialGainDays !==
          a.potentialGainDays
        ) {
          return (
            b.potentialGainDays -
            a.potentialGainDays
          );
        }

        return (
          (b.area || 0) -
          (a.area || 0)
        );
      });
  }

  function getMaximumRotation(profile) {
    const current = getCurrentRotation(profile);
    const splits = getSplitCandidates(profile);

    const allPossibleGainDays = sumBy(
      splits,
      candidate => candidate.potentialGainDays
    );

    const currentlyPracticalGainDays = sumBy(
      splits.filter(
        candidate => candidate.suitability === "ready"
      ),
      candidate => candidate.potentialGainDays
    );

    return {
      days: round(
        current.days + allPossibleGainDays,
        1
      ),
      weeks: formatWeeks(
        current.days + allPossibleGainDays
      ),
      currentlyPracticalDays: round(
        current.days +
          currentlyPracticalGainDays,
        1
      ),
      currentlyPracticalWeeks: formatWeeks(
        current.days +
          currentlyPracticalGainDays
      ),
      baseRotationDays: current.days,
      additionalPossibleDays: round(
        allPossibleGainDays,
        1
      ),
      additionalCurrentlyPracticalDays: round(
        currentlyPracticalGainDays,
        1
      ),
      activePaddockCount:
        current.activePaddockCount,
      currentShiftCount:
        current.grazingShiftCount,
      maximumShiftCount:
        current.grazingShiftCount +
        sumBy(
          splits,
          candidate =>
            candidate.additionalShiftCount
        ),
      currentlyPracticalShiftCount:
        current.grazingShiftCount +
        sumBy(
          splits.filter(
            candidate =>
              candidate.suitability === "ready"
          ),
          candidate =>
            candidate.additionalShiftCount
        ),
      splitCandidates: splits
    };
  }

  function calendarSeason(date = new Date()) {
    const month = date.getMonth() + 1;

    if ([9, 10, 11].includes(month)) {
      return "spring";
    }

    if ([12, 1, 2].includes(month)) {
      return "summer";
    }

    if ([3, 4, 5].includes(month)) {
      return "autumn";
    }

    return "winter";
  }

  function currentPlanYear(date = new Date()) {
    return date.getFullYear();
  }

  function getPlan(profile, season, year) {
    const plan = valueAt(
      profile,
      `seasonalPlans.${season}.${year}`,
      null
    );

    return isPlainObject(plan)
      ? plan
      : null;
  }

  function getPlanRotationTask(plan) {
    if (!isPlainObject(plan)) return null;

    const tasks = isPlainObject(plan.tasks)
      ? plan.tasks
      : {};

    const candidates = [
      tasks.grazingRotation,
      tasks.rotation,
      tasks.grazing,
      tasks.currentRotation
    ];

    return candidates.find(isPlainObject) || null;
  }

  function targetFromPlan(profile, season, year) {
    const plan = getPlan(profile, season, year);
    const task = getPlanRotationTask(plan);
    const data = isPlainObject(task?.data)
      ? task.data
      : {};

    const days = positiveNumber(
      firstDefined([
        data.targetRotationDays,
        data.plannedRotationDays,
        data.rotationTargetDays,
        valueAt(
          getMyBlock(profile),
          `grazing.seasonalTargets.${season}.days`
        ),
        valueAt(
          getMyBlock(profile),
          `grazing.seasonalTargets.${season}.preferredDays`
        )
      ])
    );

    const weeks = positiveNumber(
      firstDefined([
        data.targetRotationWeeks,
        data.plannedRotationWeeks,
        data.rotationTargetWeeks,
        valueAt(
          getMyBlock(profile),
          `grazing.seasonalTargets.${season}.weeks`
        ),
        valueAt(
          getMyBlock(profile),
          `grazing.seasonalTargets.${season}.preferredWeeks`
        )
      ])
    );

    const resolvedDays =
      days !== null
        ? days
        : weeks !== null
          ? weeks * 7
          : null;

    if (resolvedDays === null) {
      return null;
    }

    return {
      minimumDays: resolvedDays,
      preferredDays: resolvedDays,
      maximumDays: resolvedDays,
      source: "seasonal_plan",
      sourceLabel: `${capitalise(
        season
      )} Plan`,
      season,
      year,
      planUpdatedAt:
        plan?.meta?.updatedAt ||
        task?.meta?.updatedAt ||
        null
    };
  }

  function capitalise(value) {
    const text = String(value || "");

    return text
      ? text.charAt(0).toUpperCase() +
          text.slice(1)
      : "";
  }

  function resolveGrowthCondition(profile) {
    const grazing = getGrazing(profile);

    return normaliseToken(
      firstDefined([
        grazing.currentGrowthCondition,
        grazing.grassGrowthCondition,
        grazing.growthCondition,
        grazing.grassStatus,
        grazing.currentGrassGrowth,
        valueAt(
          profile,
          "currentFarmState.grassStatus"
        )
      ])
    );
  }

  function resolveSelectedSeason(profile) {
    const grazing = getGrazing(profile);

    const selected = normaliseToken(
      firstDefined([
        grazing.currentSeason,
        grazing.currentGrazingSeason,
        grazing.seasonalStage
      ])
    );

    return SEASONS.includes(selected)
      ? selected
      : null;
  }

  function getRotationTarget(profile, options = {}) {
    const now =
      options.now instanceof Date
        ? options.now
        : new Date();

    const selectedSeason =
      normaliseToken(options.season) ||
      resolveSelectedSeason(profile);

    const season = SEASONS.includes(selectedSeason)
      ? selectedSeason
      : calendarSeason(now);

    const year =
      integer(options.year, null) ||
      currentPlanYear(now);

    const planTarget = targetFromPlan(
      profile,
      season,
      year
    );

    if (planTarget) {
      return {
        ...planTarget,
        minimumWeeks: formatWeeks(
          planTarget.minimumDays
        ),
        preferredWeeks: formatWeeks(
          planTarget.preferredDays
        ),
        maximumWeeks: formatWeeks(
          planTarget.maximumDays
        ),
        growthCondition:
          resolveGrowthCondition(profile) ||
          null
      };
    }

    const growthCondition =
      normaliseToken(options.growthCondition) ||
      resolveGrowthCondition(profile);

    const growthTarget =
      GROWTH_CONDITION_TARGETS[
        growthCondition
      ];

    if (growthTarget) {
      return {
        ...growthTarget,
        minimumWeeks: formatWeeks(
          growthTarget.minimumDays
        ),
        preferredWeeks: formatWeeks(
          growthTarget.preferredDays
        ),
        maximumWeeks: formatWeeks(
          growthTarget.maximumDays
        ),
        source: "growth_condition",
        sourceLabel: growthTarget.label,
        season,
        year,
        growthCondition
      };
    }

    const calendarTarget =
      DEFAULT_TARGETS[season];

    return {
      ...calendarTarget,
      minimumWeeks: formatWeeks(
        calendarTarget.minimumDays
      ),
      preferredWeeks: formatWeeks(
        calendarTarget.preferredDays
      ),
      maximumWeeks: formatWeeks(
        calendarTarget.maximumDays
      ),
      source: "calendar_season",
      sourceLabel: `${capitalise(
        season
      )} default`,
      season,
      year,
      growthCondition:
        growthCondition || null
    };
  }

  function parseDateOnly(value) {
    if (
      typeof value !== "string" ||
      !/^\d{4}-\d{2}-\d{2}$/.test(value)
    ) {
      return null;
    }

    const [year, month, day] = value
      .split("-")
      .map(Number);

    const date = new Date(
      year,
      month - 1,
      day,
      12,
      0,
      0,
      0
    );

    return Number.isNaN(date.getTime())
      ? null
      : date;
  }

  function normaliseDate(value) {
    if (value instanceof Date) {
      return Number.isNaN(value.getTime())
        ? null
        : new Date(value.getTime());
    }

    const dateOnly = parseDateOnly(value);

    if (dateOnly) return dateOnly;

    if (!value) return null;

    const date = new Date(value);

    return Number.isNaN(date.getTime())
      ? null
      : date;
  }

  function calendarDayDifference(later, earlier) {
    const laterUtc = Date.UTC(
      later.getFullYear(),
      later.getMonth(),
      later.getDate()
    );

    const earlierUtc = Date.UTC(
      earlier.getFullYear(),
      earlier.getMonth(),
      earlier.getDate()
    );

    return Math.floor(
      (laterUtc - earlierUtc) / DAY_MS
    );
  }

  function addCalendarDays(date, days) {
    const result = new Date(date.getTime());

    result.setDate(
      result.getDate() + days
    );

    return result;
  }

  function positiveModulo(value, divisor) {
    if (!divisor) return 0;

    return (
      ((value % divisor) + divisor) %
      divisor
    );
  }

  function getCurrentPaddock(profile) {
    const grazing = getGrazing(profile);
    const entries = buildRotationEntries(profile);

    if (!entries.length) return null;

    const selectedId = firstDefined([
      grazing.currentPaddockId,
      grazing.currentGrazingPaddockId
    ]);

    const selected = entries.find(
      entry => entry.paddockId === selectedId
    );

    if (selected) {
      return {
        paddockId: selected.paddockId,
        paddockName: selected.paddockName,
        order: selected.order,
        durationDays: round(
          selected.durationDays,
          1
        ),
        source: "selected"
      };
    }

    const projected = getProjectedPosition(
      profile
    );

    if (!projected?.currentPaddock) {
      return null;
    }

    return {
      ...projected.currentPaddock,
      source: "projected"
    };
  }

  function getNextPaddock(profile) {
    const entries = buildRotationEntries(profile);

    if (!entries.length) return null;

    const current = getCurrentPaddock(profile);

    if (!current) {
      const first = entries[0];

      return {
        paddockId: first.paddockId,
        paddockName: first.paddockName,
        order: first.order,
        durationDays: round(
          first.durationDays,
          1
        )
      };
    }

    const currentIndex = entries.findIndex(
      entry =>
        entry.paddockId === current.paddockId
    );

    const nextIndex =
      currentIndex < 0
        ? 0
        : (currentIndex + 1) %
          entries.length;

    const next = entries[nextIndex];

    return {
      paddockId: next.paddockId,
      paddockName: next.paddockName,
      order: next.order,
      durationDays: round(
        next.durationDays,
        1
      )
    };
  }

  function getProjectedPosition(
    profile,
    options = {}
  ) {
    const entries = buildRotationEntries(profile);
    const grazing = getGrazing(profile);

    if (!entries.length) {
      return {
        available: false,
        reason: "no_active_paddocks",
        currentPaddock: null,
        nextPaddock: null,
        daysIntoCurrentStay: null,
        daysUntilMove: null,
        projectedMoveDate: null,
        rotationDay: null,
        completedRotations: null
      };
    }

    const startDate = normaliseDate(
      firstDefined([
        options.startDate,
        grazing.currentRotationStartDate
      ])
    );

    const now = normaliseDate(
      firstDefined([
        options.now,
        new Date()
      ])
    );

    const selectedPaddockId = firstDefined([
      options.startPaddockId,
      grazing.currentPaddockId,
      entries[0].paddockId
    ]);

    const startIndex = Math.max(
      0,
      entries.findIndex(
        entry =>
          entry.paddockId ===
          selectedPaddockId
      )
    );

    if (!startDate || !now) {
      const current = entries[startIndex];
      const next =
        entries[
          (startIndex + 1) %
            entries.length
        ];

      return {
        available: false,
        reason: "rotation_start_date_missing",
        currentPaddock: {
          paddockId: current.paddockId,
          paddockName:
            current.paddockName,
          order: current.order,
          durationDays: round(
            current.durationDays,
            1
          )
        },
        nextPaddock: {
          paddockId: next.paddockId,
          paddockName:
            next.paddockName,
          order: next.order,
          durationDays: round(
            next.durationDays,
            1
          )
        },
        daysIntoCurrentStay: null,
        daysUntilMove: null,
        projectedMoveDate: null,
        rotationDay: null,
        completedRotations: null
      };
    }

    const totalRotationDays = sumBy(
      entries,
      entry => entry.durationDays
    );

    if (totalRotationDays <= 0) {
      return {
        available: false,
        reason: "rotation_duration_unavailable",
        currentPaddock: null,
        nextPaddock: null,
        daysIntoCurrentStay: null,
        daysUntilMove: null,
        projectedMoveDate: null,
        rotationDay: null,
        completedRotations: null
      };
    }

    const elapsedCalendarDays = Math.max(
      0,
      calendarDayDifference(now, startDate)
    );

    const completedRotations = Math.floor(
      elapsedCalendarDays /
        totalRotationDays
    );

    let position = positiveModulo(
      elapsedCalendarDays,
      totalRotationDays
    );

    let currentIndex = startIndex;
    let daysIntoCurrentStay = 0;

    for (
      let step = 0;
      step < entries.length;
      step += 1
    ) {
      const entry =
        entries[
          (startIndex + step) %
            entries.length
        ];

      if (position < entry.durationDays) {
        currentIndex =
          (startIndex + step) %
          entries.length;
        daysIntoCurrentStay = position;
        break;
      }

      position -= entry.durationDays;
    }

    const current = entries[currentIndex];
    const next =
      entries[
        (currentIndex + 1) %
          entries.length
      ];

    const daysUntilMove = Math.max(
      0,
      Math.ceil(
        current.durationDays -
          daysIntoCurrentStay
      )
    );

    const projectedMoveDate =
      addCalendarDays(
        now,
        daysUntilMove
      );

    return {
      available: true,
      reason: null,
      startDate:
        startDate.toISOString(),
      asAt: now.toISOString(),
      elapsedCalendarDays,
      completedRotations,
      rotationDay:
        round(
          positiveModulo(
            elapsedCalendarDays,
            totalRotationDays
          ) + 1,
          1
        ),
      currentPaddock: {
        paddockId: current.paddockId,
        paddockName:
          current.paddockName,
        order: current.order,
        durationDays: round(
          current.durationDays,
          1
        )
      },
      nextPaddock: {
        paddockId: next.paddockId,
        paddockName:
          next.paddockName,
        order: next.order,
        durationDays: round(
          next.durationDays,
          1
        )
      },
      daysIntoCurrentStay: round(
        daysIntoCurrentStay,
        1
      ),
      daysUntilMove,
      projectedMoveDate:
        projectedMoveDate
          .toISOString()
          .slice(0, 10),
      totalRotationDays: round(
        totalRotationDays,
        1
      ),
      totalRotationWeeks:
        formatWeeks(totalRotationDays)
    };
  }

  function getShortfall(profile, options = {}) {
    const current =
      getCurrentRotation(profile);
    const maximum =
      getMaximumRotation(profile);
    const target =
      getRotationTarget(
        profile,
        options
      );

    const preferredGap =
      target.preferredDays -
      current.days;

    const minimumGap =
      target.minimumDays -
      current.days;

    const maximumGap =
      target.maximumDays -
      current.days;

    let status = "within_target";

    if (current.days < target.minimumDays) {
      status = "short";
    } else if (
      current.days >
      target.maximumDays
    ) {
      status = "long";
    }

    return {
      status,
      currentDays: current.days,
      currentWeeks: current.weeks,
      targetMinimumDays:
        target.minimumDays,
      targetPreferredDays:
        target.preferredDays,
      targetMaximumDays:
        target.maximumDays,
      targetMinimumWeeks:
        target.minimumWeeks,
      targetPreferredWeeks:
        target.preferredWeeks,
      targetMaximumWeeks:
        target.maximumWeeks,
      shortfallToMinimumDays:
        round(
          Math.max(0, minimumGap),
          1
        ),
      shortfallToMinimumWeeks:
        formatWeeks(
          Math.max(0, minimumGap)
        ),
      shortfallToPreferredDays:
        round(
          Math.max(0, preferredGap),
          1
        ),
      shortfallToPreferredWeeks:
        formatWeeks(
          Math.max(0, preferredGap)
        ),
      excessAboveMaximumDays:
        round(
          Math.max(0, -maximumGap),
          1
        ),
      excessAboveMaximumWeeks:
        formatWeeks(
          Math.max(0, -maximumGap)
        ),
      maximumPossibleDays:
        maximum.days,
      maximumPossibleWeeks:
        maximum.weeks,
      targetReachable:
        maximum.days >=
        target.minimumDays,
      target,
      current,
      maximum
    };
  }

  function getPressureCandidates(profile) {
    const important =
      getImportantPaddocks(profile);
    const preferredId =
      important.preferredPressurePaddockId;

    const entries =
      buildRotationEntries(profile);

    const areas = entries
      .map(entry => entry.area)
      .filter(value => value !== null);

    const largestArea = areas.length
      ? Math.max(...areas)
      : null;

    return entries
      .filter(entry => {
        const answer = normaliseToken(
          entry.paddock
            .canHandleExtraPressure
        );

        return (
          answer === "yes" ||
          entry.paddock.canHandleExtraPressure ===
            true ||
          entry.paddockId === preferredId
        );
      })
      .map(entry => {
        const paddock = entry.paddock;
        const reasons = Array.isArray(
          paddock.pressureReason
        )
          ? paddock.pressureReason
          : Array.isArray(
                paddock.pressureReasons
              )
            ? paddock.pressureReasons
            : [];

        let score = 0;

        if (
          entry.paddockId === preferredId
        ) {
          score += 20;
        }

        reasons.forEach(reason => {
          score +=
            PRESSURE_REASON_WEIGHTS[
              normaliseToken(reason)
            ] || 0;
        });

        if (
          largestArea !== null &&
          entry.area === largestArea
        ) {
          score += 4;
        }

        if (
          normaliseToken(
            paddock.drainage
          ) === "good"
        ) {
          score += 3;
        }

        if (
          normaliseToken(
            paddock.shelter
          ) === "well_sheltered"
        ) {
          score += 2;
        }

        if (
          normaliseToken(
            paddock.waterAvailable
          ) === "yes"
        ) {
          score += 2;
        }

        if (
          normaliseToken(
            paddock.dryingWindExposure
          ) === "yes"
        ) {
          score -= 2;
        }

        return {
          paddockId: entry.paddockId,
          paddockName: entry.paddockName,
          area: entry.area,
          durationDays: round(
            entry.durationDays,
            1
          ),
          preferred:
            entry.paddockId ===
            preferredId,
          score,
          reasons: reasons.map(
            normaliseToken
          ),
          drainage:
            paddock.drainage || null,
          shelter:
            paddock.shelter || null,
          waterAvailable:
            paddock.waterAvailable || null
        };
      })
      .sort((a, b) => {
        if (b.score !== a.score) {
          return b.score - a.score;
        }

        return (
          (b.area || 0) -
          (a.area || 0)
        );
      });
  }

  function daysLabel(days) {
    const roundedDays = round(days, 1);

    return roundedDays === 1
      ? "1 day"
      : `${roundedDays} days`;
  }

  function recommendationObject({
    code,
    priority,
    status,
    headline,
    currentSituation,
    whyItMatters,
    action,
    whyThis,
    alternative,
    selectedPaddock = null,
    potentialGainDays = 0,
    confidence = "medium",
    watch = null
  }) {
    return {
      code,
      priority,
      status,
      headline,
      currentSituation,
      whyItMatters,
      action,
      whyThis,
      alternative,
      selectedPaddock,
      potentialGainDays:
        round(potentialGainDays, 1),
      potentialGainWeeks:
        formatWeeks(
          potentialGainDays
        ),
      confidence,
      watch
    };
  }

  function getRecommendation(
    profile,
    options = {}
  ) {
    const current =
      getCurrentRotation(profile);
    const shortfall =
      getShortfall(profile, options);
    const splits =
      getSplitCandidates(profile);
    const pressure =
      getPressureCandidates(profile);
    const currentPaddock =
      getCurrentPaddock(profile);
    const nextPaddock =
      getNextPaddock(profile);
    const grazing =
      getGrazing(profile);

    if (!current.activePaddockCount) {
      return recommendationObject({
        code: "ADD_PADDOCKS",
        priority: 100,
        status: "missing_data",
        headline:
          "Add your grazing paddocks first.",
        currentSituation:
          "There are no active grazing paddocks in My Block yet.",
        whyItMatters:
          "The grazing engine needs your paddock order and normal stay before it can calculate a useful rotation.",
        action:
          "Add each permanent grazing paddock in My Block.",
        whyThis:
          "Once those paddocks are saved, your starting rotation will calculate automatically.",
        alternative:
          "Start with the paddock names and normal stay. You can refine the other details later.",
        confidence: "high"
      });
    }

    if (current.assumedDurationCount > 0) {
      const firstUnknown =
        current.entries.find(
          entry => !entry.durationKnown
        );

      return recommendationObject({
        code: "CONFIRM_NORMAL_STAY",
        priority: 90,
        status: "missing_data",
        headline:
          "Confirm the normal stay for your paddocks.",
        currentSituation:
          `${current.assumedDurationCount} ${
            current.assumedDurationCount === 1
              ? "paddock is"
              : "paddocks are"
          } currently using a one-week estimate.`,
        whyItMatters:
          "A reliable rotation calculation depends on knowing roughly how long the sheep normally spend in each paddock.",
        action:
          firstUnknown
            ? `Review the normal stay for ${firstUnknown.paddockName}.`
            : "Review the normal stay for each paddock.",
        whyThis:
          "This is the smallest missing piece of information affecting the current result.",
        alternative:
          "Leave the one-week estimate in place for now, but treat the rotation result as approximate.",
        selectedPaddock:
          firstUnknown || null,
        confidence: "high"
      });
    }

    if (shortfall.status === "short") {
      const needed =
        shortfall.shortfallToMinimumDays;

      const readySplit =
        splits.find(
          candidate =>
            candidate.suitability ===
              "ready" &&
            candidate.potentialGainDays > 0
        );

      if (readySplit) {
        const usefulGain = Math.min(
          readySplit.potentialGainDays,
          needed
        );

        return recommendationObject({
          code: "SPLIT_PADDOCK",
          priority: 70,
          status: "action",
          headline:
            `Split ${readySplit.paddockName}.`,
          currentSituation:
            `Your current rotation is about ${current.days} days. The current target begins at ${shortfall.targetMinimumDays} days.`,
          whyItMatters:
            `That leaves the block about ${daysLabel(
              needed
            )} short of the minimum recovery target.`,
          action:
            `Use temporary fencing to turn ${readySplit.paddockName} into ${readySplit.practicalShiftCount} practical grazing shifts.`,
          whyThis:
            `It is your clearest current opportunity and could add about ${daysLabel(
              readySplit.potentialGainDays
            )} to the full rotation.`,
          alternative:
            `Stay longer in ${readySplit.paddockName} this rotation instead.`,
          selectedPaddock:
            readySplit,
          potentialGainDays: usefulGain,
          confidence: "high",
          watch:
            "Check that every temporary shift has practical water access before using it."
        });
      }

      const pressureCandidate =
        pressure[0];

      if (pressureCandidate) {
        const extensionDays = Math.min(
          7,
          needed
        );

        return recommendationObject({
          code: "STAY_LONGER",
          priority: 65,
          status: "action",
          headline:
            `Stay longer in ${pressureCandidate.paddockName}.`,
          currentSituation:
            `Your current rotation is about ${current.days} days, which is shorter than the current target.`,
          whyItMatters:
            `The rest of the block would benefit from another ${daysLabel(
              needed
            )} of recovery.`,
          action:
            `Keep the sheep in ${pressureCandidate.paddockName} for roughly ${daysLabel(
              extensionDays
            )} longer this rotation, provided feed remains adequate.`,
          whyThis:
            pressureCandidate.preferred
              ? "You have already identified this as the preferred paddock for carrying a little extra pressure."
              : "This paddock has the strongest combination of size and grazing-pressure attributes in My Block.",
          alternative:
            "Use supplementary feed or another suitable paddock to create the same recovery time without overgrazing this area.",
          selectedPaddock:
            pressureCandidate,
          potentialGainDays:
            extensionDays,
          confidence:
            pressureCandidate.score >= 5
              ? "high"
              : "medium",
          watch:
            "Move sooner if pasture becomes too short, sheep lose condition or the paddock becomes wet."
        });
      }

      const blockedSplit =
        splits.find(
          candidate =>
            candidate.suitability !==
            "ready"
        );

      if (blockedSplit) {
        return recommendationObject({
          code: "PREPARE_SPLIT",
          priority: 60,
          status: "action",
          headline:
            `Prepare to split ${blockedSplit.paddockName}.`,
          currentSituation:
            `Your rotation is about ${daysLabel(
              needed
            )} short of the minimum current target.`,
          whyItMatters:
            "Creating another grazing shift would give every other paddock more recovery.",
          action:
            blockedSplit.blockedReason ||
            `Confirm whether ${blockedSplit.paddockName} can be split safely.`,
          whyThis:
            `This paddock could potentially add about ${daysLabel(
              blockedSplit.potentialGainDays
            )} to the rotation.`,
          alternative:
            "Stay a little longer in the most suitable larger paddock, provided the sheep still have adequate feed.",
          selectedPaddock:
            blockedSplit,
          potentialGainDays:
            blockedSplit.potentialGainDays,
          confidence: "medium"
        });
      }

      return recommendationObject({
        code: "ROTATION_SHORT_NO_CLEAR_ACTION",
        priority: 55,
        status: "review",
        headline:
          "Your rotation needs a little more recovery time.",
        currentSituation:
          `The current rotation is about ${current.days} days against a minimum target of ${shortfall.targetMinimumDays} days.`,
        whyItMatters:
          "Returning too quickly can gradually weaken pasture recovery, especially when growth is slow.",
        action:
          "Look for the simplest safe way to add a few days before returning to the first paddock.",
        whyThis:
          "My Block does not currently identify a clear split or extra-pressure paddock.",
        alternative:
          "Review paddock split options, temporary fencing, supplementary feed or sheep numbers before forcing the current rotation.",
        potentialGainDays: needed,
        confidence: "medium",
        watch:
          "Pasture condition should override the calendar. Do not extend a stay if the sheep are running short of feed."
      });
    }

    if (shortfall.status === "long") {
      const combinePossible =
        getActivePaddocks(profile).some(
          paddock =>
            paddock.canCombine === true ||
            normaliseToken(
              paddock.canCombine
            ) === "yes"
        );

      const fastGrowth =
        [
          "fast",
          "strong_spring_growth",
          "grass_getting_ahead"
        ].includes(
          resolveGrowthCondition(profile)
        );

      if (combinePossible || fastGrowth) {
        return recommendationObject({
          code: "SHORTEN_ROTATION",
          priority: 45,
          status: "action",
          headline:
            "Let the sheep cover a little more ground.",
          currentSituation:
            `Your current rotation is about ${current.days} days, which is longer than the upper current target of ${shortfall.targetMaximumDays} days.`,
          whyItMatters:
            "When grass is getting ahead, an overly long rotation can allow pasture quality to decline before the sheep return.",
          action:
            nextPaddock
              ? `Consider opening ${nextPaddock.paddockName} with a suitable neighbouring shift when the sheep move.`
              : "Consider combining two suitable grazing shifts for one move.",
          whyThis:
            "It shortens the rotation without changing the normal moving routine.",
          alternative:
            normaliseToken(
              grazing.willingToTopSurplus
            ) === "yes"
              ? "Keep the current rotation and top the surplus after the sheep move."
              : "Keep the current rotation for now and watch whether sheep are consistently leaving more grass behind.",
          selectedPaddock:
            nextPaddock,
          confidence:
            fastGrowth ? "high" : "medium",
          watch:
            "Only speed the rotation up when grass is genuinely getting ahead of the sheep."
        });
      }
    }

    return recommendationObject({
      code: "KEEP_CURRENT_SYSTEM",
      priority: 10,
      status: "no_change",
      headline:
        "Keep the current grazing system.",
      currentSituation:
        `Your current rotation is about ${current.days} days and sits within the current ${shortfall.target.sourceLabel.toLowerCase()} target.`,
      whyItMatters:
        "Good grazing management is usually about making small adjustments only when they are genuinely needed.",
      action:
        currentPaddock
          ? `Continue your normal routine from ${currentPaddock.paddockName}.`
          : "Continue your normal grazing routine.",
      whyThis:
        "The current rotation is providing a suitable recovery period based on the information saved in My Block.",
      alternative:
        "Adjust only if pasture condition, feed availability or sheep comfort tells you the situation has changed.",
      selectedPaddock:
        currentPaddock,
      confidence:
        current.complete
          ? "high"
          : "medium",
      watch:
        "Watch the paddocks that normally recover most slowly before the next rotation."
    });
  }

  function getSummary(profile, options = {}) {
    const currentRotation =
      getCurrentRotation(profile);
    const maximumRotation =
      getMaximumRotation(profile);
    const target =
      getRotationTarget(
        profile,
        options
      );
    const projectedPosition =
      getProjectedPosition(
        profile,
        options
      );
    const currentPaddock =
      getCurrentPaddock(profile);
    const nextPaddock =
      getNextPaddock(profile);
    const shortfall =
      getShortfall(profile, options);
    const splitCandidates =
      getSplitCandidates(profile);
    const pressureCandidates =
      getPressureCandidates(profile);
    const recommendation =
      getRecommendation(profile, options);

    return {
      engineVersion: ENGINE_VERSION,
      generatedAt:
        new Date().toISOString(),
      currentRotation,
      maximumRotation,
      target,
      projectedPosition,
      currentPaddock,
      nextPaddock,
      daysUntilMove:
        projectedPosition.daysUntilMove,
      shortfall,
      splitCandidates,
      pressureCandidates,
      recommendation
    };
  }

  const api = Object.freeze({
    version: ENGINE_VERSION,
    DEFAULT_TARGETS,
    getSummary,
    getCurrentRotation,
    getMaximumRotation,
    getRotationTarget,
    getProjectedPosition,
    getCurrentPaddock,
    getNextPaddock,
    getShortfall,
    getSplitCandidates,
    getPressureCandidates,
    getRecommendation
  });

  document.dispatchEvent(
    new CustomEvent(
      "sms:grazing-engine-ready",
      {
        detail: {
          version: ENGINE_VERSION
        }
      }
    )
  );

  return api;
})();
